import type { ShakaPlayer } from '~/composables/useSharedVideoPlayer';
import type { Ref } from 'vue';

import {
	useElementHover,
	useEventListener,
	useIntersectionObserver,
	useTimeoutFn
} from '@vueuse/core';
import {
	createPlayer,
	destroyPlayer
} from '~/composables/useSharedVideoPlayer';
import { getProxiedVideoUrl } from '~/composables/useVideoUrls';

interface UseVideoCardPlayerOptions {
	videoUrl: Ref<string>;
	isActive: Ref<boolean>;
	hoverToPlay: Ref<boolean>;
	index: Ref<number>;
}

export function useVideoCardPlayer({
	videoUrl,
	isActive,
	hoverToPlay,
	index
}: UseVideoCardPlayerOptions) {
	const tag = () => `[Video #${index.value}]`;

	const cardRef = ref<HTMLDivElement | null>(null);
	const videoRef = ref<HTMLVideoElement | null>(null);
	const shakaPlayer = shallowRef<ShakaPlayer | null>(null);

	const isHovered = useElementHover(cardRef);
	const isVideoPlaying = ref(false);
	const isVisible = ref(false);
	const isPlayerReady = ref(false);
	const isInitializing = ref(false);
	const videoProgress = ref(0);
	const isMuted = ref(true);
	let initGeneration = 0;
	let savedCurrentTime = 0;

	const shouldPlay = computed(
		() => (hoverToPlay.value && isHovered.value) || isActive.value
	);
	const isLoading = computed(() => shouldPlay.value && !isPlayerReady.value);
	const proxiedVideoUrl = computed(() => getProxiedVideoUrl(videoUrl.value));

	function handleMuteToggle() {
		isMuted.value = !isMuted.value;
		if (videoRef.value) {
			videoRef.value.muted = isMuted.value;
		}
	}

	function seekTo(ratio: number) {
		if (videoRef.value && videoRef.value.duration) {
			videoRef.value.currentTime = ratio * videoRef.value.duration;
		}
	}

	async function safePlay(video: HTMLVideoElement) {
		if (video.readyState === 0) {
			console.warn(tag(), 'safePlay: readyState=0, waiting for canplay');
			await new Promise<void>(resolve => {
				video.addEventListener('canplay', () => resolve(), { once: true });
			});
			if (!shouldPlay.value) {
				console.warn(tag(), 'safePlay: shouldPlay became false while waiting');
				return;
			}
		}

		try {
			await video.play();
		} catch (err) {
			if (err instanceof DOMException && err.name === 'NotAllowedError') {
				console.warn(tag(), 'safePlay: NotAllowedError, retrying muted');
				video.muted = true;
				isMuted.value = true;
				try {
					await video.play();
				} catch (retryErr) {
					console.error(tag(), 'safePlay: muted retry also failed', retryErr);
				}
			} else {
				console.error(tag(), 'safePlay: play() failed', err);
			}
		}
	}

	useEventListener(videoRef, 'playing', () => {
		console.warn(tag(), 'EVENT playing');
		isVideoPlaying.value = true;
	});

	useEventListener(videoRef, 'pause', () => {
		console.warn(tag(), 'EVENT pause');
		isVideoPlaying.value = false;
	});

	useEventListener(videoRef, 'waiting', () => {
		console.warn(tag(), 'EVENT waiting (buffering)', {
			currentTime: videoRef.value?.currentTime,
			readyState: videoRef.value?.readyState,
			networkState: videoRef.value?.networkState
		});
	});

	useEventListener(videoRef, 'stalled', () => {
		console.warn(tag(), 'EVENT stalled (network stall)', {
			currentTime: videoRef.value?.currentTime,
			buffered: videoRef.value?.buffered.length
				? `${videoRef.value.buffered.start(0)}-${videoRef.value.buffered.end(0)}`
				: 'none'
		});
	});

	useEventListener(videoRef, 'error', () => {
		const err = videoRef.value?.error;
		console.error(tag(), 'EVENT error', {
			code: err?.code,
			message: err?.message
		});
	});

	useEventListener(videoRef, 'timeupdate', () => {
		if (videoRef.value && videoRef.value.duration) {
			videoProgress.value =
				(videoRef.value.currentTime / videoRef.value.duration) * 100;
		}
	});

	const { start: startPreload, stop: stopPreload } = useTimeoutFn(
		() => {
			if (isVisible.value && !isPlayerReady.value) {
				initPlayer();
			}
		},
		300,
		{ immediate: false }
	);

	useIntersectionObserver(
		cardRef,
		entries => {
			const entry = entries[0];
			if (!entry) return;

			if (entry.isIntersecting) {
				console.warn(tag(), 'IO: entered viewport', {
					hoverToPlay: hoverToPlay.value,
					isPlayerReady: isPlayerReady.value,
					shouldPlay: shouldPlay.value,
					savedCurrentTime
				});
				isVisible.value = true;
				if (hoverToPlay.value) {
					startPreload();
				} else if (shouldPlay.value && !isPlayerReady.value) {
					console.warn(tag(), 'IO: shouldPlay already true, scheduling init');
					cancelPlayDebounce();
					playDebounceTimer = setTimeout(() => {
						playDebounceTimer = null;
						if (shouldPlay.value && !isPlayerReady.value && videoRef.value) {
							console.warn(tag(), 'IO debounce → initPlayer');
							stopPreload();
							initPlayer();
						}
					}, PLAY_DEBOUNCE_MS);
				}
			} else {
				console.warn(tag(), 'IO: left viewport', {
					isPlayerReady: isPlayerReady.value,
					hasPlayer: !!shakaPlayer.value,
					currentTime: videoRef.value?.currentTime
				});
				isVisible.value = false;
				cancelPlayDebounce();
				stopPreload();
				cleanupPlayer();
			}
		},
		{ rootMargin: '100px' }
	);

	async function initPlayer() {
		if (!videoRef.value || isPlayerReady.value || isInitializing.value) {
			console.warn(tag(), 'initPlayer SKIPPED', {
				hasVideoRef: !!videoRef.value,
				isPlayerReady: isPlayerReady.value,
				isInitializing: isInitializing.value
			});
			return;
		}

		console.warn(tag(), 'initPlayer START', {
			savedCurrentTime,
			shouldPlay: shouldPlay.value
		});
		const initStart = performance.now();

		isInitializing.value = true;
		const generation = ++initGeneration;

		try {
			const startTime = savedCurrentTime > 0 ? savedCurrentTime : undefined;

			const player = await createPlayer(proxiedVideoUrl.value, videoRef.value, {
				startTime
			});

			const elapsed = Math.round(performance.now() - initStart);

			if (generation !== initGeneration || !videoRef.value) {
				console.warn(
					tag(),
					`initPlayer ABORTED after ${elapsed}ms (generation mismatch or no videoRef)`
				);
				destroyPlayer(player);
				return;
			}

			shakaPlayer.value = player;
			isPlayerReady.value = true;

			console.warn(tag(), `initPlayer READY in ${elapsed}ms`, {
				shouldPlay: shouldPlay.value,
				readyState: videoRef.value?.readyState,
				duration: videoRef.value?.duration,
				currentTime: videoRef.value?.currentTime
			});

			if (shouldPlay.value && videoRef.value) {
				console.warn(tag(), 'initPlayer → calling safePlay()');
				safePlay(videoRef.value);
			}
		} catch (err) {
			const elapsed = Math.round(performance.now() - initStart);
			console.error(tag(), `initPlayer ERROR after ${elapsed}ms`, err);
		} finally {
			isInitializing.value = false;
		}
	}

	function cleanupPlayer() {
		initGeneration++;
		if (shakaPlayer.value) {
			if (videoRef.value) {
				savedCurrentTime = videoRef.value.currentTime;
			}
			console.warn(tag(), 'cleanupPlayer', {
				savedCurrentTime,
				wasPlaying: isVideoPlaying.value
			});
			destroyPlayer(shakaPlayer.value);
			shakaPlayer.value = null;
			isPlayerReady.value = false;
			isVideoPlaying.value = false;
		}
	}

	const PLAY_DEBOUNCE_MS = 200;
	let playDebounceTimer: ReturnType<typeof setTimeout> | null = null;

	function cancelPlayDebounce() {
		if (playDebounceTimer !== null) {
			clearTimeout(playDebounceTimer);
			playDebounceTimer = null;
		}
	}

	watch(
		shouldPlay,
		playing => {
			console.warn(tag(), `shouldPlay → ${playing}`, {
				hasVideoRef: !!videoRef.value,
				isPlayerReady: isPlayerReady.value,
				isInitializing: isInitializing.value,
				isVisible: isVisible.value,
				isActive: isActive.value,
				isHovered: isHovered.value,
				hoverToPlay: hoverToPlay.value
			});

			cancelPlayDebounce();

			if (!videoRef.value) {
				console.warn(tag(), 'shouldPlay watcher: no videoRef, bailing out');
				return;
			}

			if (playing) {
				if (isPlayerReady.value) {
					console.warn(tag(), 'shouldPlay → safePlay() (already ready)');
					safePlay(videoRef.value);
				} else {
					console.warn(
						tag(),
						`shouldPlay → debouncing initPlayer (${PLAY_DEBOUNCE_MS}ms)`
					);
					playDebounceTimer = setTimeout(() => {
						playDebounceTimer = null;
						if (shouldPlay.value && !isPlayerReady.value && videoRef.value) {
							console.warn(tag(), 'shouldPlay debounce → initPlayer');
							stopPreload();
							initPlayer();
						}
					}, PLAY_DEBOUNCE_MS);
				}
			} else if (isPlayerReady.value) {
				console.warn(tag(), 'shouldPlay → pause()');
				videoRef.value.pause();
			}
		},
		{ immediate: true }
	);

	onBeforeUnmount(() => {
		cancelPlayDebounce();
		stopPreload();
		cleanupPlayer();
	});

	return {
		cardRef,
		videoRef,
		isVideoPlaying,
		isVisible,
		isLoading,
		shouldPlay,
		videoProgress,
		isMuted,
		handleMuteToggle,
		seekTo
	};
}
